import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileCheck, Loader2, AlertTriangle, Search, ArrowUpDown, ArrowUp, ArrowDown, Download } from "lucide-react";
import { US_STATES, US_STATE_OPTIONS } from "@/lib/usStates";

const STATUS_FILTERS = ["PENDING_REVIEW", "APPROVED", "EXPIRING_SOON", "REJECTED", "EXPIRED", "ALL"];
const FILTER_LABELS = {
  PENDING_REVIEW: "Pending review", APPROVED: "Approved", EXPIRING_SOON: "Expiring soon",
  REJECTED: "Rejected", EXPIRED: "Expired", ALL: "All",
};
const STATUS_BADGE = { PENDING_REVIEW: "badge-reserved", APPROVED: "badge-available", REJECTED: "badge-reserved", EXPIRED: "badge-neutral" };
const EXTRACTION_BADGE = { COMPLETED: "badge-available", PARTIAL: "badge-reserved", EXTRACTION_FAILED: "badge-reserved", PENDING: "badge-neutral" };
const EXTRACTION_LABEL = {
  COMPLETED: "Extraction complete", PARTIAL: "Partial extraction",
  EXTRACTION_FAILED: "Extraction failed — enter values manually below", PENDING: "Extraction in progress",
};

// EVERY extracted field is admin-editable — one Save mechanism for the whole set.
const REVIEW_FIELDS = [
  { key: "business_name", label: "Business / legal name" },
  { key: "dba", label: "DBA" },
  { key: "certificate_number", label: "Certificate / resale number" },
  { key: "tax_id", label: "Tax ID" },
  { key: "state", label: "State / jurisdiction", state: true },
  { key: "issue_date", label: "Issue / effective date", date: true },
  { key: "expiration_date", label: "Expiration date", date: true },
];

const fmtDate = (v) => (v ? new Date(`${String(v).slice(0, 10)}T12:00:00`).toLocaleDateString("en-US") : "—");
const fmtUploaded = (v) => (v ? new Date(v).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");

export default function AdminCertificates() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("PENDING_REVIEW");
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [sort, setSort] = useState("uploaded");
  const [dir, setDir] = useState("desc");
  const [active, setActive] = useState(null);
  const [history, setHistory] = useState([]);
  const [fileUrl, setFileUrl] = useState(null);
  const [form, setForm] = useState({});
  const [note, setNote] = useState("");
  const [acting, setActing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [zoom, setZoom] = useState(1);  // RC49-CERT-006
  const [verified, setVerified] = useState(false);  // RC49-CERT-005: explicit verification override
  const debounce = useRef(null);
  const REQUIRED = ["business_name", "certificate_number", "state", "expiration_date"];
  const missingRequired = REQUIRED.filter((k) => !(form[k] || "").trim());
  const canApprove = missingRequired.length === 0 || verified;

  const load = useCallback(() => {
    const params = { sort, direction: dir };
    if (filter !== "ALL") params.status = filter;
    if (search.trim()) params.q = search.trim();
    if (stateFilter) params.state = stateFilter;
    api.get("/admin/certificates", { params }).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [filter, search, stateFilter, sort, dir]);

  useEffect(() => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(load, 250);
    return () => clearTimeout(debounce.current);
  }, [load]);

  const toggleSort = (key) => {
    if (sort === key) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSort(key); setDir(key === "uploaded" ? "desc" : "asc"); }
  };

  const openReview = async (row) => {
    setNote(""); setZoom(1); setVerified(false);
    const r = await api.get(`/admin/certificates/${row.id}`);
    setActive(r.data);
    const eff = r.data.effective || {};
    setForm({
      business_name: eff.business_name || "",
      dba: eff.dba || "",
      certificate_number: eff.certificate_number || "",
      tax_id: eff.tax_id || "",
      state: eff.state || r.data.state || "",
      issue_date: eff.issue_date || r.data.issue_date || "",
      expiration_date: eff.expiration_date || r.data.expiration_date || "",
    });
    api.get("/admin/certificates", { params: { dealer_id: row.dealer_id, status: "ALL" } })
      .then((h) => setHistory(h.data)).catch(() => setHistory([]));
    try {
      // axios (not raw fetch): the interceptor self-heals an expired session via
      // refresh before this request fails — the exact root cause of "Original unavailable"
      const resp = await api.get(`/admin/certificates/${row.id}/file`, { responseType: "blob" });
      setFileUrl(URL.createObjectURL(new Blob([resp.data], { type: r.data.content_type || "application/octet-stream" })));
    } catch {
      setFileUrl(null);
    }
  };

  const closeReview = () => {
    if (fileUrl) URL.revokeObjectURL(fileUrl);
    setFileUrl(null);
    setActive(null);
  };

  const saveFields = async () => {
    setSaving(true);
    try {
      const payload = {};
      REVIEW_FIELDS.forEach(({ key }) => { if (form[key]) payload[key] = form[key]; });
      const r = await api.post(`/admin/certificates/${active.id}/fields`, payload);
      setActive(r.data);
      toast.success("Reviewed values saved — review flags recalculated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save fields");
    } finally {
      setSaving(false);
    }
  };

  const review = async (action) => {
    if (action === "REJECTED" && !note.trim()) {
      toast.error("A note is required when rejecting — it is shown to the dealer");
      return;
    }
    setActing(true);
    try {
      const finalNote = action === "APPROVED" && verified && missingRequired.length ? `${note.trim()} [Manually verified; approved without: ${missingRequired.join(", ")}]`.trim() : note.trim();
      await api.post(`/admin/certificates/${active.id}/review`, { action, note: finalNote });
      toast.success(action === "APPROVED" ? "Certificate approved — dealer notified" : "Certificate rejected — dealer notified");
      window.dispatchEvent(new Event("notifications:changed"));  // nav badges refresh immediately after the action
      closeReview();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Review failed");
    } finally {
      setActing(false);
    }
  };

  const SortHeader = ({ skey, label }) => (
    <th className="px-3 py-2 text-left">
      <button type="button" onClick={() => toggleSort(skey)} data-testid={`cert-sort-${skey}`}
        className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-warmgrey hover:text-charcoal">
        {label}
        {sort === skey ? (dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
      </button>
    </th>
  );

  return (
    <div data-testid="admin-certificates-page">
      <div className="flex items-center gap-3">
        <FileCheck className="h-5 w-5 text-terracotta" />
        <h1 className="font-display text-2xl font-medium text-charcoal">Certificates</h1>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        Dealer resale certificates by state/jurisdiction — review queue and compliance library.
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="certificate-filters">
        {STATUS_FILTERS.map((s) => (
          <button key={s} type="button" onClick={() => setFilter(s)} data-testid={`certificate-filter-${s.toLowerCase().replace(/_/g, "-")}`}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
              filter === s ? "bg-charcoal text-white" : "border border-border bg-white text-warmgrey hover:border-charcoal"}`}>
            {FILTER_LABELS[s]}
          </button>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <div className="relative min-w-0 flex-1 sm:max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-warmgrey" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="certificate-search-input"
            placeholder="Search dealer, legal name, DBA, certificate #, tax ID, state…"
            className="h-11 bg-white pl-9 focus-visible:ring-terracotta" />
        </div>
        <Select value={stateFilter || "ALL"} onValueChange={(v) => setStateFilter(v === "ALL" ? "" : v)}>
          <SelectTrigger className="h-11 w-44 bg-white" data-testid="certificate-state-filter">
            <SelectValue placeholder="All states" />
          </SelectTrigger>
          <SelectContent className="max-h-72 bg-white">
            <SelectItem value="ALL">All states</SelectItem>
            {US_STATE_OPTIONS.map((s) => (
              <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {rows === null ? (
        <div className="mt-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-terracotta" /></div>
      ) : rows.length === 0 ? (
        <p className="mt-8 text-sm text-muted-foreground" data-testid="certificate-empty">
          No certificates match this view.
        </p>
      ) : (
        <>
          <div className="mt-5 hidden overflow-x-auto rounded-lg border border-border bg-white md:block">
            <table className="w-full text-sm" data-testid="certificate-table">
              <thead>
                <tr className="border-b border-border">
                  <SortHeader skey="dealer" label="Dealer" />
                  <SortHeader skey="state" label="State" />
                  <SortHeader skey="status" label="Status" />
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-warmgrey">Certificate #</th>
                  <SortHeader skey="expiration" label="Expiration" />
                  <SortHeader skey="uploaded" label="Uploaded" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} onClick={() => openReview(r)} data-testid={`certificate-row-${r.id}`}
                    className="cursor-pointer border-b border-limestone/60 transition-colors last:border-0 hover:bg-sand/50">
                    <td className="px-3 py-3 font-medium text-charcoal">{r.dealer_name || "—"}</td>
                    <td className="px-3 py-3 text-warmgrey">{r.state ? (US_STATES[r.state] || r.state) : "—"}</td>
                    <td className="px-3 py-3">
                      <span className={`badge-status ${STATUS_BADGE[r.status] || "badge-neutral"}`}>{FILTER_LABELS[r.status] || r.status}</span>
                      {r.flags?.length > 0 && <AlertTriangle className="ml-1.5 inline h-3.5 w-3.5 text-status-reserved" aria-label="Review flags" />}
                    </td>
                    <td className="px-3 py-3 font-mono text-xs text-warmgrey">{r.certificate_number || "—"}</td>
                    <td className="px-3 py-3 text-warmgrey">
                      {r.expiration_date ? fmtDate(r.expiration_date) : "—"}
                      {r.days_remaining != null && r.status === "APPROVED" && (
                        <span className={`ml-1 text-xs ${r.days_remaining <= 30 ? "font-medium text-status-reserved" : "text-muted-foreground"}`}>
                          ({r.days_remaining}d)
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-warmgrey">{fmtUploaded(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-5 space-y-2 md:hidden" data-testid="certificate-cards-mobile">
            {rows.map((r) => (
              <button key={r.id} type="button" onClick={() => openReview(r)} data-testid={`certificate-row-${r.id}`}
                className="w-full rounded-lg border border-border bg-white p-4 text-left transition-colors hover:bg-sand/50">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium text-charcoal">{r.dealer_name || "—"}</p>
                  <span className={`badge-status ${STATUS_BADGE[r.status] || "badge-neutral"}`}>{FILTER_LABELS[r.status] || r.status}</span>
                </div>
                <p className="mt-1 text-xs text-warmgrey">
                  {r.state ? (US_STATES[r.state] || r.state) : "State unconfirmed"}
                  {r.certificate_number ? ` · #${r.certificate_number}` : ""}
                  {r.expiration_date ? ` · exp ${fmtDate(r.expiration_date)}` : ""}
                </p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">Uploaded {fmtUploaded(r.created_at)}</p>
              </button>
            ))}
          </div>
        </>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && closeReview()}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-border bg-white sm:max-w-3xl" data-testid="cert-review-dialog">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl font-medium text-charcoal">
                  {active.display_name || "Certificate review"}
                </DialogTitle>
              </DialogHeader>
              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Original document</p>
                  {fileUrl ? (
                    active.content_type === "application/pdf" ? (
                      <iframe src={`${fileUrl}#view=FitH`} title="Certificate document" className="mt-2 h-[360px] w-full rounded-md border border-border bg-white md:h-[480px]" data-testid="cert-review-document" />
                    ) : (
                      <div className="mt-2 max-h-[480px] overflow-auto rounded-md border border-border bg-sand/30" data-testid="cert-review-document-wrap">
                        <img src={fileUrl} alt="Certificate document" style={{ width: `${zoom * 100}%`, maxWidth: "none" }} className="block" data-testid="cert-review-document" />
                      </div>
                    )
                  ) : (
                    <div className="mt-2 flex h-[360px] items-center justify-center rounded-md border border-dashed border-border text-sm text-muted-foreground" data-testid="cert-doc-unavailable">
                      Original unavailable — contact platform support.
                    </div>
                  )}
                  {fileUrl && (
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs" data-testid="cert-doc-tools">
                      {active.content_type !== "application/pdf" && (
                        <>
                          <button type="button" onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))} className="rounded border border-border px-2 py-0.5" data-testid="cert-zoom-out">−</button>
                          <span className="num">{Math.round(zoom * 100)}%</span>
                          <button type="button" onClick={() => setZoom((z) => Math.min(4, z + 0.25))} className="rounded border border-border px-2 py-0.5" data-testid="cert-zoom-in">+</button>
                          <button type="button" onClick={() => setZoom(1)} className="rounded border border-border px-2 py-0.5" data-testid="cert-zoom-fit">Fit width</button>
                        </>
                      )}
                      <a href={fileUrl} target="_blank" rel="noreferrer" className="font-medium text-terracotta hover:underline" data-testid="cert-open-new-tab">Open / View in new tab{active.content_type === "application/pdf" ? " (page navigation, zoom)" : ""}</a>
                    </div>
                  )}
                  <p className="mt-2 text-xs text-muted-foreground">
                    Original file: <span className="font-medium text-charcoal">{active.original_filename}</span>
                    {" "}({(active.size / 1024).toFixed(0)} KB)
                  </p>
                  {fileUrl && (
                    <a href={fileUrl} download={active.original_filename || "certificate"} data-testid="cert-download-original"
                      className="mt-1 inline-flex items-center gap-1.5 text-xs font-semibold text-terracotta hover:text-terracotta-hover">
                      <Download className="h-3.5 w-3.5" /> Download exact original
                    </a>
                  )}
                  <p className="mt-1 text-xs text-muted-foreground">Uploaded {new Date(active.created_at).toLocaleString("en-US")}</p>
                  {active.jurisdiction_confirmed && (
                    <p className="mt-1 text-xs font-medium text-status-available" data-testid="cert-jurisdiction-confirmed">
                      Jurisdiction confirmed by Admin{active.state ? `: ${US_STATES[active.state] || active.state}` : ""}
                    </p>
                  )}
                  {history.length > 1 && (
                    <div className="mt-4" data-testid="cert-history">
                      <p className="text-xs font-semibold uppercase tracking-wider text-warmgrey">History (this dealer)</p>
                      <ul className="mt-2 space-y-1.5">
                        {history.map((h) => (
                          <li key={h.id} className="flex flex-wrap items-center gap-2 text-xs text-warmgrey">
                            <span className="num">{new Date(h.created_at).toLocaleDateString()}</span>
                            <span className={`badge-status ${STATUS_BADGE[h.status] || "badge-neutral"}`}>{FILTER_LABELS[h.status] || h.status}</span>
                            {h.state && <span className="badge-status badge-neutral">{h.state}</span>}
                            <span className="num">{h.original_filename}</span>
                            {h.replaced_by_certificate_id && <span>→ replaced</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`badge-status ${STATUS_BADGE[active.status] || "badge-neutral"}`}>{FILTER_LABELS[active.status] || active.status}</span>
                    <span className={`badge-status ${EXTRACTION_BADGE[active.extraction_status] || "badge-neutral"}`} data-testid="cert-extraction-status">
                      {EXTRACTION_LABEL[active.extraction_status] || active.extraction_status}
                    </span>
                  </div>
                  {active.flags?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5" data-testid="cert-flags">
                      {active.flags.map((f) => (
                        <span key={f} className="inline-flex items-center gap-1 rounded-full bg-[#D4A373]/15 px-2.5 py-0.5 text-xs text-[#8a6435]">
                          <AlertTriangle className="h-3 w-3" />{f.replace(/_/g, " ")}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="mt-4 space-y-3" data-testid="cert-fields-form">
                    <p className="text-xs font-semibold uppercase tracking-wider text-warmgrey">
                      Reviewed values — compare against the original and correct anything
                    </p>
                    {REVIEW_FIELDS.map((f) => (
                      <div key={f.key}>
                        <label className="text-xs font-medium text-charcoal" htmlFor={`cert-field-${f.key}`}>{f.label}{REQUIRED.includes(f.key) && <span className="text-status-sold"> *</span>}</label>
                        <p className="text-[11px] text-muted-foreground">
                          Extracted (source): <span className="font-mono" data-testid={`cert-ai-${f.key}`}>{active.extracted?.[f.key] || "Not extracted — needs review"}</span>
                        </p>
                        <Input
                          id={`cert-field-${f.key}`}
                          type={f.date ? "date" : "text"}
                          maxLength={f.state ? 2 : undefined}
                          value={form[f.key] || ""}
                          onChange={(e) => setForm((v) => ({ ...v, [f.key]: f.state ? e.target.value.toUpperCase() : e.target.value }))}
                          className="mt-0.5 h-10 bg-white focus-visible:ring-terracotta"
                          data-testid={`cert-field-${f.key}`}
                        />
                      </div>
                    ))}
                    <Button onClick={saveFields} disabled={saving} data-testid="cert-save-fields"
                      className="h-10 w-full bg-charcoal text-white hover:bg-charcoal/90">
                      {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                      Save reviewed values
                    </Button>
                    <p className="text-[11px] text-muted-foreground">
                      The original AI extraction is preserved; your reviewed values become the authoritative
                      certificate values after approval.
                    </p>
                  </div>
                  {active.status === "PENDING_REVIEW" && (
                    <div className="sticky bottom-0 -mx-2 mt-4 border-t border-border bg-white px-2 pb-1 pt-4" data-testid="cert-review-actions">
                      <label className="text-xs font-semibold uppercase tracking-wider text-warmgrey" htmlFor="cert-note-input">
                        Review note <span className="normal-case text-muted-foreground">(required to reject — shown to dealer)</span>
                      </label>
                      <Textarea id="cert-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                        className="mt-1 bg-white focus-visible:ring-terracotta" data-testid="cert-note-input"
                        placeholder="e.g. Verified against state registry" />
                      {missingRequired.length > 0 && (
                        <div className="mt-2 rounded-md border border-status-reserved/40 bg-[#D4A373]/10 p-2 text-xs text-[#8a6435]" data-testid="cert-missing-required">
                          Missing required values: {missingRequired.map((k) => REVIEW_FIELDS.find((f) => f.key === k)?.label).join(", ")}.
                          <label className="mt-1 flex items-center gap-2"><input type="checkbox" checked={verified} onChange={(e) => setVerified(e.target.checked)} data-testid="cert-verified-override" /> I verified this certificate manually and approve without these values (recorded in the review note).</label>
                        </div>
                      )}
                      <div className="mt-3 flex gap-3">
                        <Button onClick={() => review("APPROVED")} disabled={acting || !canApprove} data-testid="cert-approve"
                          title={canApprove ? "Approve" : "Complete the required values or confirm manual verification"}
                          className="h-11 flex-1 bg-terracotta text-white hover:bg-terracotta-hover">
                          {acting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Approve
                        </Button>
                        <Button onClick={() => review("REJECTED")} disabled={acting} variant="outline" data-testid="cert-reject"
                          className="h-11 flex-1 border-status-reserved/50 text-status-reserved hover:bg-[#D4A373]/10">
                          Reject
                        </Button>
                      </div>
                    </div>
                  )}
                  {active.status === "REJECTED" && active.review_note && (
                    <p className="mt-4 rounded-md border border-status-reserved/40 bg-[#D4A373]/10 p-3 text-sm text-[#8a6435]" data-testid="cert-review-note">
                      Rejection note shown to dealer: {active.review_note}
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
